import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Loader2, ArrowRight, ShieldCheck, Sparkles, Calendar } from 'lucide-react';
import MarketingHeader from '../components/marketing/MarketingHeader';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

/**
 * Modern split-screen sign-in.
 *
 * Left (lg+ only): immersive marketing rail — gradient mesh + 3 quick
 *   value-prop chips. Lets us sell while authenticating.
 * Right: the form itself. Big, focused, with helper text + a hosted-
 *   checkout-style "show password" eye, and a friendly fallback link to /signup.
 *
 * Top of every page: the unified MarketingHeader so visitors can always
 * navigate to Home / Features / Pricing / Sign Up — the very thing the
 * old auth page broke.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // If the workspace was cancelled, the login response carries a
  // structured detail we surface as an inline "Reactivate" CTA rather
  // than a plain error string.
  const [cancelledState, setCancelledState] = useState(null); // { workspace_name, cancelled_at }
  const [reactivating, setReactivating] = useState(false);

  // Prefill the username field when the user landed here from the
  // sidebar's "Switch Account" flow (which links to `/login?u=<name>`).
  // Only runs once per URL change so a typing user isn't overwritten.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const u = params.get('u');
    if (u) setUsername(u);
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setCancelledState(null);
    setSubmitting(true);
    try {
      await login(username, password);
      const dest = (location.state && location.state.from) || '/app';
      navigate(dest);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // subscription_cancelled: backend returns a JSON-encoded detail
      // string with { code, workspace_name, cancelled_at, ... }. Parse
      // it, keep the password in state, and flip into reactivate UI.
      if (typeof detail === 'string' && detail.startsWith('{')) {
        try {
          const parsed = JSON.parse(detail);
          if (parsed?.code === 'subscription_cancelled') {
            setCancelledState({
              workspace_name: parsed.workspace_name || '',
              cancelled_at: parsed.cancelled_at || null,
            });
            return;
          }
        } catch { /* fall through to generic error */ }
      }
      if (detail) setError(formatApiErrorDetail(detail));
      else if (err.response?.status === 429) setError('Too many login attempts. Please wait a few minutes.');
      else if (err.code === 'ERR_NETWORK' || !err.response) setError('Unable to connect to server. Please check your connection and try again.');
      else setError('Invalid username or password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true); setError('');
    try {
      // Use the api client directly so we can hit /auth/reactivate
      // (which returns the same shape as /auth/login), then hydrate the
      // auth context via setSessionFromTokens.
      const api = (await import('../lib/api')).default;
      const { data } = await api.post('/auth/reactivate', { username, password });
      const { setSessionFromTokens } = await import('../contexts/AuthContext').then(m => m.useAuth?.() || {});
      // Fallback: reload the app so AuthContext refetches /auth/me.
      if (data?.access_token) {
        localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
      }
      window.location.href = '/app';
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not reactivate. Check your password and try again.');
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-screen bg-[#0D1520] text-white" style={{ fontFamily: "'Manrope', 'Satoshi', system-ui, sans-serif" }}>
      <MarketingHeader />

      <div className="min-h-screen grid lg:grid-cols-[1fr_minmax(420px,520px)]">
        {/* Left rail — visible from lg+ */}
        <aside className="relative hidden lg:flex flex-col justify-between pt-28 pb-12 px-12 xl:pt-32 xl:pb-16 xl:px-16 overflow-hidden">
          {/* Gradient mesh background */}
          <div aria-hidden className="absolute inset-0">
            <div className="absolute -top-32 -left-24 w-[640px] h-[640px] rounded-full"
                 style={{ background: 'radial-gradient(circle at center, rgba(203,163,59,0.22), transparent 60%)' }} />
            <div className="absolute -bottom-40 -right-32 w-[720px] h-[720px] rounded-full"
                 style={{ background: 'radial-gradient(circle at center, rgba(185,138,44,0.16), transparent 60%)' }} />
            <div className="absolute inset-0 opacity-[0.07]"
                 style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
          </div>

          <div className="relative">
            <p className="text-[11px] font-bold tracking-[0.24em] uppercase text-[#CBA33B] mb-3">Mantle Suite</p>
            <h2 className="text-4xl xl:text-5xl font-black leading-[1.05] tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Welcome back.<br />
              <span className="bg-gradient-to-br from-[#E5C46A] via-[#CBA33B] to-[#B98A2C] bg-clip-text text-transparent">Let&apos;s get to work.</span>
            </h2>
            <p className="mt-5 text-[15px] text-zinc-400 max-w-md leading-relaxed">
              The all-in-one business suite for independent contractors. Invoices, contracts, scheduling, AI — one login, every job.
            </p>
          </div>

          <div className="relative space-y-3 max-w-md">
            <FeatureRow icon={Sparkles} title="Ask Mantle agent" body="Tell the AI what to do — it drafts estimates, contracts and tasks for you." />
            <FeatureRow icon={Calendar} title="Schedule + dispatch" body="Drag-and-drop calendar with SMS reminders to clients and crew." />
            <FeatureRow icon={ShieldCheck} title="Tax-ready books" body="Receipt OCR, mileage, 1099 prep — when April rolls around, you&apos;re ready." />
          </div>
        </aside>

        {/* Right column — form */}
        <main className="flex items-center justify-center px-5 sm:px-10 pt-28 pb-16 lg:pt-16">
          <div className="w-full max-w-md">
            {/* Mobile-only logo */}
            <div className="lg:hidden flex justify-center mb-8">
              <img src="/mantle-logo.png" alt="Mantle" className="w-20 h-20 object-contain" />
            </div>

            <p className="text-[11px] font-bold tracking-[0.24em] uppercase text-[#CBA33B] mb-2">Sign in</p>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Hello again.
            </h1>
            <p className="text-[13.5px] text-zinc-400 mb-8">
              New here?{' '}
              <Link to="/signup" data-testid="login-to-signup-link" className="text-[#CBA33B] hover:text-[#D8B257] font-semibold transition-colors">
                Start your free 14-day trial →
              </Link>
            </p>

            {error && (
              <div data-testid="auth-error" role="alert"
                className="mb-5 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-[13px] leading-snug">
                {error}
              </div>
            )}

            {cancelledState && (
              <div data-testid="reactivate-panel" role="alert"
                className="mb-5 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-100">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-400">Subscription cancelled</p>
                <p className="mt-2 text-[13.5px] leading-snug">
                  <strong>{cancelledState.workspace_name || 'This workspace'}</strong> was cancelled
                  {cancelledState.cancelled_at ? ` on ${new Date(cancelledState.cancelled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}.
                  Your data is preserved — reactivate to sign back in.
                </p>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    data-testid="reactivate-btn"
                    onClick={handleReactivate}
                    disabled={reactivating || !password}
                    className="inline-flex items-center justify-center h-10 px-4 rounded-lg bg-[#CBA33B] hover:bg-[#B98A2C] text-[#0D1520] font-bold text-[13px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reactivating ? 'Reactivating…' : 'Reactivate & Sign in'}
                  </button>
                  <button
                    type="button"
                    data-testid="dismiss-reactivate-btn"
                    onClick={() => setCancelledState(null)}
                    className="inline-flex items-center justify-center h-10 px-4 rounded-lg border border-white/[0.08] text-slate-300 hover:bg-white/[0.03] text-[13px]"
                  >
                    Not now
                  </button>
                </div>
                {!password && (
                  <p className="mt-2 text-[11.5px] text-amber-300/80">Re-enter your password above, then tap Reactivate.</p>
                )}
              </div>
            )}

            {/* "Continue with Google" — kicks off Emergent Google OAuth.
                REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
                REDIRECT URLS, THIS BREAKS THE AUTH. */}
            <button
              type="button"
              data-testid="google-signin-btn"
              onClick={() => {
                const redirectUrl = window.location.origin + '/auth/callback';
                window.location.href =
                  'https://auth.emergentagent.com/?redirect=' + encodeURIComponent(redirectUrl);
              }}
              className="w-full h-12 mb-4 bg-white hover:bg-zinc-100 active:bg-zinc-200 text-[#0D1520] font-semibold text-[14px] rounded-lg border border-white/10 transition-all hover:shadow-[0_6px_20px_rgba(255,255,255,0.08)] flex items-center justify-center gap-2.5"
            >
              <GoogleGlyph />
              <span>Continue with Google</span>
            </button>

            <div className="relative my-4 flex items-center">
              <span className="flex-1 h-px bg-white/[0.06]" />
              <span className="px-3 text-[10.5px] uppercase tracking-[0.2em] font-bold text-zinc-500">or</span>
              <span className="flex-1 h-px bg-white/[0.06]" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField label="Email or username">
                <input
                  data-testid="login-username-input"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="username"
                  className="w-full bg-white/[0.04] border border-white/[0.08] focus:border-[#CBA33B] focus:bg-white/[0.06] focus:ring-2 focus:ring-[#CBA33B]/20 outline-none rounded-lg h-12 px-3.5 text-[14.5px] text-white placeholder:text-zinc-500 transition"
                />
              </FormField>

              <FormField
                label="Password"
                aux={
                  <Link to="/forgot-password" data-testid="login-forgot-password-link"
                    className="text-[11.5px] text-[#CBA33B] hover:text-[#D8B257] font-semibold transition-colors">
                    Forgot password?
                  </Link>
                }
              >
                <div className="relative">
                  <input
                    data-testid="login-password-input"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••••"
                    required
                    autoComplete="current-password"
                    className="w-full bg-white/[0.04] border border-white/[0.08] focus:border-[#CBA33B] focus:bg-white/[0.06] focus:ring-2 focus:ring-[#CBA33B]/20 outline-none rounded-lg h-12 pl-3.5 pr-11 text-[14.5px] text-white placeholder:text-zinc-500 transition"
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Hide password' : 'Show password'} tabIndex={-1}
                    className="absolute inset-y-0 right-0 px-3.5 flex items-center text-zinc-500 hover:text-[#CBA33B] transition-colors">
                    {showPw ? <EyeOff size={17} strokeWidth={1.9} /> : <Eye size={17} strokeWidth={1.9} />}
                  </button>
                </div>
              </FormField>

              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={submitting || !username || !password}
                className="group w-full h-12 mt-2 bg-[#CBA33B] hover:bg-[#D8B257] disabled:bg-[#CBA33B]/40 disabled:cursor-not-allowed text-[#0D1520] font-bold text-[14px] rounded-lg transition-all hover:shadow-[0_8px_28px_rgba(203,163,59,0.4)] flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <><Loader2 size={16} className="animate-spin" /> Signing you in…</>
                ) : (
                  <>Sign in <ArrowRight size={15} strokeWidth={2.8} className="group-hover:translate-x-0.5 transition-transform" /></>
                )}
              </button>
            </form>

            <p className="text-center text-[11.5px] text-zinc-500 mt-8">
              By signing in you agree to our{' '}
              <Link to="/terms" className="text-zinc-400 hover:text-white">Terms</Link>{' '}and{' '}
              <Link to="/privacy" className="text-zinc-400 hover:text-white">Privacy Policy</Link>.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

function FeatureRow({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-white/[0.025] border border-white/[0.05]">
      <div className="w-9 h-9 rounded-lg bg-[#CBA33B]/15 flex items-center justify-center shrink-0">
        <Icon size={16} className="text-[#CBA33B]" strokeWidth={2.2} />
      </div>
      <div className="min-w-0">
        <p className="text-[13px] font-bold text-white">{title}</p>
        <p className="text-[12px] text-zinc-400 mt-0.5 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function FormField({ label, aux, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[11.5px] font-bold uppercase tracking-[0.12em] text-zinc-400">{label}</label>
        {aux}
      </div>
      {children}
    </div>
  );
}

// Official Google "G" mark — 4-color, no outline. Rendered inline so we
// don't have to ship an extra asset for one button.
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
